<script setup lang="ts">
import { ViewCompactItemType, type RenderableFile } from '@lib/types/files'
import useThemeVars from '@lib/composables/useThemeVars'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import { openItem } from '@browser/util/file'
import type { Favorite } from '@openapi'
const props = defineProps<{
  item: RenderableFile & { opacity?: number }
  isEven: boolean
  isFavorite?: boolean
}>()

const themeVars = useThemeVars()
const { parseNameOnly } = useFolderNameDescription()

function getName(item: RenderableFile | Favorite): string {
  if (props.isFavorite) {
    const favorite = item as Favorite

    // If it's a file favorite, show the file name
    if (favorite.file?.name) {
      return favorite.file.name
    }

    // If it's a folder favorite, show the parsed folder name
    if (favorite.folder?.name) {
      return parseNameOnly(favorite.folder.name)
    }

    // Fallback to the favorite's own name
    return favorite.name ?? ''
  }

  // For non-favorite items, check if it's a folder type
  const itemName = item.name ?? ''
  if ('type' in item && item.type === ViewCompactItemType.Folder) {
    return parseNameOnly(itemName)
  }

  return itemName
}
</script>

<template>
  <div
    class="min-h-[60px] overflow-hidden flex cursor-pointer items-center p-2 gap-1 border-rd-1"
    :class="{ 'is-even': isEven }"
    :style="{ opacity: item.opacity || 1 }"
    @click="openItem(item as RenderableFile, $event)"
  >
    <CFileThumbnail :height="44" :icon-size="24" :item="item" :width="60" />
    <div class="ml-4 max-w-full">
      {{ getName(item) }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.is-even {
  background: v-bind('themeVars.primary6_1');
}

.quick-access-item:hover {
  background: v-bind('themeVars.primary6');
}

.quick-access-item__icon {
  color: v-bind('themeVars.primary3');
}
</style>
